import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    signal,
    ViewEncapsulation,
    QueryList,
    ViewChildren,
    inject,
    input,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Bind } from "../../../decorators/bind.decorator";
import { Item } from "../../../pojo/Item";
import { S25Util } from "../../../util/s25-util";
import { Proto } from "../../../pojo/Proto";
import ServiceStatus = Proto.ServiceStatus;
import { ContactService } from "../../../services/contact.service";
import { UserprefService } from "../../../services/userpref.service";
import { OrganizationService } from "../../../services/organization.service";
import CustAttrCode = CustomAttributes.CustAttrCode;
import { SpaceService } from "../../../services/space.service";
import { AttachmentService } from "../../../services/attachment.service";
import { S25EditableCustomAttributeComponent } from "../../s25-editable/s25.editable-custom-attribute/s25.editable-custom-attribute.component";
import { ResourceMicroService } from "../../../services/resource.micro.service";
import { EventMicroService } from "../../../services/event.micro.service";
import { AlertService } from "../../../services/alert.service";
import { S25EventDetailsInlineEditService } from "../event/s25.event.details.inline.edit.service";
import { S25CustomAttributeItemFormatter } from "../../s25-custom-attribute/s25.custom.attribute.item.formatter";

type CustAttrObj = CustomAttributes.CustAttrObj & { isNew?: boolean; showSaveHint?: boolean };

@TypeManagerDecorator("s25-ng-object-details-custom-attr")
@Component({
    selector: "s25-ng-object-details-custom-attr",
    template: `
        @if (init()) {
            @if (!customAttrs?.length && !canEdit) {
                none
            } @else {
                @for (attr of customAttrs; track attr.itemId) {
                    @let hasFadePreview = attr.itemTypeId === AttrType.LongText && attr.itemName?.length > 350;
                    @let isNumericItemType = typeof attr.itemTypeId === "number";
                    @let needReadOnlyEl =
                        isNumericItemType || attr.itemTypeId === AttrType.Time || attr.itemTypeId === AttrType.DateTime;
                    @let attrValueFormat =
                        isNumericItemType
                            ? { itemId: attr.itemId, itemName: attr.itemName, itemTypeId: attr.itemTypeId }
                            : attr.itemTypeId === AttrType.Image
                              ? attr.imageId
                              : formatAttrEditValue(attr);
                    <!-- mobile app needs z-index values for object dropdowns, datepickers, etc for overlay issues -->
                    @let increasedZIndex = isNumericItemType || dateTimeTypes.includes(attr.itemTypeId);

                    <div
                        class="attr-container c-margin-bottom--half"
                        [ngClass]="{
                            'fade-preview-container': hasFadePreview && fadePreview(),
                            'long-text': attr.itemTypeId === AttrType.LongText,
                            'file-type': attr.itemTypeId === AttrType.URL,
                            'image-type': attr.itemTypeId === AttrType.Image,
                            'read-only': !canEdit && needReadOnlyEl,
                            'increase-zindex': increasedZIndex,
                        }"
                        [style.--index-val]="(increasedZIndex && customAttrs.length - $index) || 0"
                        (click)="applyFadePreview(hasFadePreview)"
                    >
                        @if (canEdit) {
                            <button
                                class="c-textButton"
                                (click)="save(attr, 'del', attrValueFormat)"
                                [attr.aria-label]="'Remove ' + attr.itemName"
                            >
                                <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                            </button>
                        }
                        @if (needReadOnlyEl && !canEdit) {
                            <label class="attribute-label">{{ attr.itemLabel }}<span class="colon">:</span></label>
                            @if (isNumericItemType) {
                                <s25-item
                                    [itemTypeId]="attr.itemTypeId"
                                    [itemId]="attr.itemId"
                                    [itemName]="attr.itemName"
                                    [includeTypeIcon]="true"
                                    [hideLabel]="true"
                                />
                            } @else if (attr.itemTypeId === AttrType.Time) {
                                <div>{{ S25Util.date.parseTime(attr.itemName) | timeFormat: timePref }}</div>
                            } @else if (attr.itemTypeId === AttrType.DateTime) {
                                <div>{{ S25Util.date.parseDropTZ(attr.itemName) | dateFormat: dateTimePref }}</div>
                            }
                        }
                        @if (canEdit || (!needReadOnlyEl && !canEdit)) {
                            @let onCommitCallback = canEdit ? onCommit.bind(this, attr) : undefined;
                            <s25-ng-editable-custom-attribute
                                [attributeId]="attr.custAttrId"
                                [attributeType]="attr.itemTypeId"
                                [attributeName]="attr.itemLabel"
                                [value]="attrValueFormat"
                                [multiVal]="attr.multi_val ? 1 : 0"
                                [readOnly]="!canEdit"
                                [hasCommit]="true"
                                [onCommit]="onCommitCallback"
                                [fadePreview]="hasFadePreview"
                                [hasUploadFile]="true"
                                [itemTypeId]="itemTypeId"
                                [objectId]="itemId"
                                [blockDetailsNav]="true"
                                [resolveCustomAttributeLabel]="resolveCustomAttributeLabel"
                            ></s25-ng-editable-custom-attribute>
                        }
                    </div>
                    @if (attr.isNew && attr.showSaveHint) {
                        <p class="ngFinePrint ngRed">Edit this custom attribute to add it</p>
                    }
                }
                @if (canEdit) {
                    @if (showDropdown()) {
                        <s25-custom-attr-dropdown
                            [itemTypeId]="itemTypeId"
                            [filter]="filterCustomAttrs"
                            [useSecurity]="true"
                            [eventTypeId]="eventTypeId"
                            (valueChange)="addCustomAttr($event)"
                        />
                    } @else {
                        <button
                            class="aw-button aw-button--primary c-margin-top--half add-cust-attr-btn"
                            (click)="showDropdown.set(true)"
                        >
                            Add a Custom Attribute
                        </button>
                    }
                    <s25-ng-fading-success-msg
                        [(message)]="successMsg"
                        class="c-margin-top--quarter"
                    ></s25-ng-fading-success-msg>
                }
            }
        }
    `,
    host: {
        "[class.read-only]": "!canEdit",
        "[class.card-appearance]": "appearance() === 'card'",
    },
    styles: `
        .attribute-label {
            overflow-wrap: break-word;
        }

        @container custom-attr (width <= 600px) {
            .attr-container {
                align-items: flex-start !important;

                .attributeRow {
                    display: flex !important;
                    gap: 5px;
                    flex-direction: column;
                    align-items: flex-start !important;

                    & > label {
                        position: relative;
                        top: 3px;
                    }

                    & > div {
                        width: 100%;

                        .ngDropdownPaginated {
                            width: 100% !important;
                        }

                        s25-timepicker,
                        s25-datepicker {
                            display: inline-block;
                            margin-top: 10px;
                        }
                    }
                }
            }

            .add-cust-attr-btn {
                width: 100%;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class S25ObjectDetailsCustomAttrComponent implements OnInit {
    @Input({ required: true }) customAttrs: CustAttrObj[];
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) itemTypeId: number;
    @Input({ required: true }) canEdit: boolean;
    @Input() eventTypeId: number;
    readonly appearance = input<"default" | "card">("default");
    @ViewChildren(S25EditableCustomAttributeComponent)
    custAttrComponent: QueryList<S25EditableCustomAttributeComponent>;

    init = signal(false);
    showDropdown = signal(false);
    successMsg = signal("");
    fadePreview = signal(true);
    AttrType = CustomAttributes.type;
    timePref: string;
    dateTimePref: string;
    dateTimeTypes: CustAttrCode[] = [this.AttrType.Date, this.AttrType.DateTime, this.AttrType.Time];

    private readonly inlineEditService = inject(S25EventDetailsInlineEditService, { optional: true });

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit() {
        const [timePref, dateTimePref] = await Promise.all([
            UserprefService.getS25Timeformat(),
            UserprefService.getS25DateTimeformat(),
        ]);
        this.timePref = timePref;
        this.dateTimePref = dateTimePref;
        this.init.set(true);
    }

    @Bind
    formatAttrEditValue(attr: CustAttrObj) {
        if (
            attr.itemTypeId === this.AttrType.DateTime ||
            attr.itemTypeId === this.AttrType.Date ||
            attr.itemTypeId === this.AttrType.Time ||
            attr.itemTypeId === this.AttrType.Duration
        ) {
            return S25CustomAttributeItemFormatter.getFormat(
                { itemTypeId: attr.itemTypeId },
                attr.itemName ?? undefined,
            );
        }
        return attr.itemName;
    }

    @Bind
    filterCustomAttrs(item: DropDownItem) {
        return !this.customAttrs.find((attr) => attr.custAttrId === item.val);
    }

    @Bind
    resolveCustomAttributeLabel(custAttrId: number): string | undefined {
        return this.customAttrs.find((attr) => attr.custAttrId === custAttrId)?.itemLabel;
    }

    addCustomAttr(attr: DropDownItem) {
        const { itemId, itemName, custAtrbType, multi_val, imageId, sort_order } = attr;
        this.customAttrs.push({
            custAttrId: +itemId,
            imageId: imageId,
            itemId: +itemId,
            itemLabel: itemName,
            itemName: null,
            itemTypeId: custAtrbType,
            multi_val: multi_val,
            sort_order: sort_order,
            isNew: true,
            showSaveHint: true,
        });
        this.showDropdown.set(false);
    }

    async getFileVal(value: any, attr: CustAttrObj) {
        if (attr.itemTypeId !== this.AttrType.URL || !value?.fileRef) return null;
        const sanitizedName = AttachmentService.sanitizeFilenameSpaces(value.name) ?? value.name;
        const newValue = `object_type=${this.itemTypeId}&object_id=${this.itemId}&atrb_id=${attr.itemId}&file_name=${sanitizedName}`;
        const [_, err] = await S25Util.Maybe(
            AttachmentService.postFile(this.itemTypeId, this.itemId, value.fileRef, sanitizedName, attr.itemId),
        );
        if (err) {
            this.onError(err);
            return null;
        }
        this.custAttrComponent.forEach((caComp) => {
            if (caComp.attributeId() === attr.itemId) {
                caComp.value.set(newValue);
                caComp.onRefresh(newValue);
            }
        });
        return newValue;
    }

    stringifyMultiVal(value: DropDownItem[]) {
        return JSON.stringify(value?.map((item) => ({ item: item.itemName })) ?? []);
    }

    async save(attr: CustAttrObj, status: ServiceStatus, value: any) {
        if (S25Util.isUndefined(value) && attr.isNew) {
            this.customAttrs = this.customAttrs.filter((item) => attr.custAttrId !== item.custAttrId);
            this.cd.markForCheck();
            return;
        }

        // Automatically delete attribute from object if empty, but keep it in the list as though it wasn't deleted
        let keepAttribute = false;
        if (status !== "del" && this.isEmpty(attr, value)) {
            status = "del";
            attr.showSaveHint = false;
            keepAttribute = true;
        }

        const fileVal = await this.getFileVal(value, attr);
        if (attr.multi_val) value = this.stringifyMultiVal(value);
        if ([Item.Ids.Event, Item.Ids.Location, Item.Ids.Resource].includes(this.itemTypeId)) {
            return this.saveMicro(attr, status, fileVal ?? value, keepAttribute);
        }
        return this.saveCustomAttr(attr, status, fileVal ?? value, keepAttribute);
    }

    isEmpty(attr: CustAttrObj, value: any) {
        switch (attr.itemTypeId) {
            case this.AttrType.Text:
            case this.AttrType.LongText:
            case this.AttrType.URL: {
                if (attr.multi_val && !value?.length) return true;
                return !value;
            }
            default:
                return false;
        }
    }

    async saveCustomAttr(attr: CustAttrObj, status: ServiceStatus, value: any, keepAttribute: boolean) {
        const nodeName = this.itemTypeId === Item.Ids.Contact ? "attribute" : "custom_attribute";
        const payload: Partial<Record<typeof nodeName, { status: ServiceStatus; attribute_id: number }>> = {};
        payload[nodeName] = {
            status: status,
            attribute_id: attr.custAttrId,
            ...(status !== "del" && {
                attribute_value: value.imageId ?? value.itemId ?? value,
            }),
        };
        let updateAttrsPromise;
        switch (this.itemTypeId) {
            case Item.Ids.Contact:
                updateAttrsPromise = ContactService.putContactMinimal(this.itemId, payload);
                break;
            case Item.Ids.Organization:
                updateAttrsPromise = OrganizationService.putOrganization(this.itemId, payload);
                break;
        }
        const [_, error] = await S25Util.Maybe(updateAttrsPromise);
        if (error) return console.error(error);
        this.onAttrUpdate(attr, status, value, keepAttribute);
    }

    async saveMicro(attr: CustAttrObj, status: ServiceStatus, value: any, keepAttribute: boolean) {
        let payload: any = {
            attributes: [
                {
                    attributeId: attr.custAttrId,
                    ...(status !== "del" && { value: value.imageId ?? value.itemId ?? value }),
                },
            ],
        };
        if (status === "del") {
            payload = { remove: payload };
        }
        if ([Item.Ids.Location, Item.Ids.Resource].includes(this.itemTypeId)) {
            payload = { id: this.itemId, ...payload };
        }
        let updateAttrsPromise;
        switch (this.itemTypeId) {
            case Item.Ids.Location:
                updateAttrsPromise = SpaceService.microUpdate(payload);
                break;
            case Item.Ids.Resource:
                updateAttrsPromise = ResourceMicroService.saveResource(payload);
                break;
            case Item.Ids.Event:
                updateAttrsPromise = this.inlineEditService
                    ? this.inlineEditService.runInlineEditScenarios(this.itemId, () =>
                          EventMicroService.putEventAttributes(payload, this.itemId),
                      )
                    : EventMicroService.putEventAttributes(payload, this.itemId);
                break;
        }
        const [_, error] = await S25Util.Maybe(updateAttrsPromise);
        if (error) return console.error(error);
        this.onAttrUpdate(attr, status, value, keepAttribute);
    }

    async onAttrUpdate(attr: CustAttrObj, status: ServiceStatus, value: any, keepAttribute: boolean) {
        this.successMsg.set(
            `${attr.itemLabel} successfully ${status === "del" && !keepAttribute ? "removed" : "updated"}`,
        );
        attr.isNew &&= false;
        if (attr.itemTypeId === this.AttrType.LongText) attr.itemName = value; // apply fade preview for long text types if applicable on update
        if (status === "del") {
            if (!keepAttribute) {
                this.customAttrs = this.customAttrs.filter((item) => attr.custAttrId !== item.custAttrId);
            }
            //if url type and upload file, when delete CA also delete file
            if (attr.itemTypeId === this.AttrType.URL)
                await AttachmentService.delFiles(this.itemTypeId, S25Util.array.forceArray(this.itemId), attr.itemId);
        }
        this.cd.markForCheck();
    }

    applyFadePreview(hasFadePreview: boolean) {
        if (hasFadePreview && !S25Util.isTextHighlight()) this.fadePreview.set(!this.fadePreview());
    }

    @Bind
    onCommit(attr: CustAttrObj, value: string) {
        this.save(attr, attr.isNew ? "new" : "mod", value);
    }

    onError(error: any) {
        const errorMsg = S25Util.errorText(error);
        if (errorMsg) {
            if (errorMsg?.toLowerCase().includes("already uploaded a file with this name")) {
                return AlertService.alert(
                    "That file name is already used for another attachment on this object. Choose a different name, or remove or replace the existing file first.",
                );
            }
            return alert(errorMsg);
        } else {
            return alert("An unexpected error occurred.");
        }
    }

    protected readonly S25Util = S25Util;
}
